import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from 'modules/app/components/App';
import { moveJourneyContentsAndFoldersMutation } from 'api/mutations/workflowIndex';

import {
  AncestorIdData,
  AncestorNameData,
  FolderId,
  FolderIdData,
  FolderNameData,
} from 'modules/domains/foldering';

import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { NamedFolder, ResponseWrapper } from 'ui/toast';

import {
  GraphQLMoveToWorkflowFolderError,
  GraphQLMoveToWorkflowFolderKnownFailures,
  GraphQLMoveToWorkflowFolderMutationResponse,
  GraphQLMoveJourneyToFolderMutationVariables,
  GraphQLMoveToWorkflowFolderSuccess,
  WorkflowId,
  WorkflowIdData,
  WorkflowNameData,
  WorkflowTypeData,
} from '../types';
import { formatFolderName } from '../utils/formatFolderName';

const displayText = {
  genericError: 'Could not move items',
};

const keyItemsById = <ItemType extends { id: number }>(items: ItemType[]) =>
  items.reduce<Record<ItemType['id'], ItemType>>(
    (previousValue, item) => ({ ...previousValue, [item.id]: item }),
    {} as Record<ItemType['id'], ItemType>,
  );

const showSuccess = (
  destinationFolderId: FolderId,
  success: GraphQLMoveToWorkflowFolderSuccess,
  foldersById: Record<FolderId, FolderNameData & FolderIdData>,
  workflowsById: Record<WorkflowId, WorkflowNameData & WorkflowIdData>,
  dispatch: AppDispatch,
) => {
  const destinationName = formatFolderName(foldersById[destinationFolderId]?.name || '');
  if (foldersById[success.id]) {
    dispatch(
      addPositiveToast(
        <ResponseWrapper>
          <NamedFolder name={foldersById[success.id]?.name || ''} /> was successfully moved into the{' '}
          <NamedFolder name={destinationName} /> folder.
        </ResponseWrapper>,
      ),
    );
  } else if (workflowsById[success.id]) {
    dispatch(
      addPositiveToast(
        <ResponseWrapper>
          Journey <strong>{workflowsById[success.id]?.name || ''}</strong> was successfully moved
          into the <NamedFolder name={destinationName} /> folder.
        </ResponseWrapper>,
      ),
    );
  }
};

const showKnownError = (
  destinationFolderId: FolderId,
  err: GraphQLMoveToWorkflowFolderError,
  foldersById: Record<FolderId, FolderNameData & FolderIdData>,
  workflowsById: Record<WorkflowId, WorkflowNameData & WorkflowIdData>,
  dispatch: AppDispatch,
) => {
  const destinationName = formatFolderName(foldersById[destinationFolderId]?.name || '');
  const sourceName = workflowsById[err.id]?.name || foldersById[err.id]?.name || '';
  switch (err.__typename) {
    case GraphQLMoveToWorkflowFolderKnownFailures.DestinationNotFoundFailure: {
      dispatch(
        addNegativeToastWithErrorId(
          <ResponseWrapper>
            <NamedFolder name={sourceName} /> folder can not be moved as{' '}
            <NamedFolder name={destinationName} /> does not exist.
          </ResponseWrapper>,
          'DestinationNotFoundFailure',
        ),
      );
      break;
    }

    case GraphQLMoveToWorkflowFolderKnownFailures.FolderNameConflictFailure: {
      dispatch(
        addNegativeToastWithErrorId(
          <ResponseWrapper>
            The folder can not be moved into <NamedFolder name={destinationName} /> as a folder with
            that name already exists. This could also be because the folder is already a child
            folder of <NamedFolder name={destinationName} />.
          </ResponseWrapper>,
          'FolderNameConflictFailure',
        ),
      );
      break;
    }

    case GraphQLMoveToWorkflowFolderKnownFailures.FolderIsAncestorOfDestinationFailure: {
      dispatch(
        addNegativeToastWithErrorId(
          <ResponseWrapper>
            <NamedFolder name={sourceName} /> folder can not be moved into{' '}
            <NamedFolder name={sourceName} /> folder or one of its child folders.
          </ResponseWrapper>,
          'FolderIsAncestorOfDestinationFailure',
        ),
      );
      break;
    }

    case GraphQLMoveToWorkflowFolderKnownFailures.FolderNameNotFoundFailure: {
      dispatch(
        addNegativeToastWithErrorId(
          'Could not move: FolderNameConflictFailure',
          'FolderNameNotFoundFailure',
        ),
      );
      break;
    }

    case GraphQLMoveToWorkflowFolderKnownFailures.ParentNotFoundFailure: {
      dispatch(
        addNegativeToastWithErrorId(
          'Could not move: ParentNotFoundFailure',
          'ParentNotFoundFailure',
        ),
      );
      break;
    }

    case GraphQLMoveToWorkflowFolderKnownFailures.WorkflowDoesNotExistFailure: {
      dispatch(
        addNegativeToastWithErrorId(
          'Could not move: WorkflowDoesNotExistFailure',
          'WorkflowDoesNotExistFailure',
        ),
      );
      break;
    }

    default: {
      dispatch(
        addNegativeToastWithErrorId(
          <ResponseWrapper>
            <NamedFolder name={sourceName} /> folder could not be moved.
          </ResponseWrapper>,
          'UnknownMoveFailure',
        ),
      );
    }
  }
};

export const useMoveToFolder = () => {
  const dispatch = useDispatch();
  const [moveToFolderMutation] = useMutation<
    GraphQLMoveToWorkflowFolderMutationResponse,
    GraphQLMoveJourneyToFolderMutationVariables
  >(moveJourneyContentsAndFoldersMutation);
  const moveToFolderFn = useCallback(
    async (
      folders: (FolderNameData & FolderIdData)[],
      workflows: (WorkflowNameData & WorkflowIdData & WorkflowTypeData)[],
      destination: AncestorIdData & AncestorNameData,
    ) => {
      const workflowsById = keyItemsById(workflows);
      const foldersById = keyItemsById([...folders, destination]);
      const mutationVariables = {
        folderIds: folders.map(({ id }) => id),
        content: workflows.map(({ id, workflowType }) => ({
          id,
          type: workflowType,
        })),
        destinationId: destination.id,
      };

      try {
        const { data: mutationData, errors: mutationErrors } = await moveToFolderMutation({
          variables: mutationVariables,
        });
        if (!mutationData?.moveContentsAndFolders || mutationErrors) {
          dispatch(addNegativeToastWithErrorId(displayText.genericError, 'moveContentsAndFolders'));
          return;
        }

        mutationData.moveContentsAndFolders.errors.forEach((err) =>
          showKnownError(destination.id, err, foldersById, workflowsById, dispatch),
        );
        mutationData.moveContentsAndFolders.movedContent.forEach((success) =>
          showSuccess(destination.id, success, foldersById, workflowsById, dispatch),
        );
      } catch (error) {
        dispatch(addNegativeToastWithErrorId(displayText.genericError, 'moveContentsAndFolders'));
      }
    },
    [moveToFolderMutation],
  );

  return moveToFolderFn;
};
